import React, { useState, useEffect, useContext } from 'react'
import { Stepper, Variant as StepperVariant } from 'stepper'
import ChooseAPlan from './choose-a-plan/ChooseAPlan'
import PickANumber from './pick-a-number/PickANumber'
import CheckoutPage from './checkout/CheckoutPage'
import { useHistory } from 'react-router-dom'
import { useMediaQuery, useTheme } from '@material-ui/core'
import { makeStyles, withStyles } from '@material-ui/core'
import StepConnector from '@material-ui/core/StepConnector'
import * as GTM from '../util/GTM'
import LeadContext from '../LeadContext'

const useStyles = makeStyles(theme => {
    return {
        stepper: {
            background: 'transparent',
            marginBottom: '15px',
            [theme.breakpoints.up('md')]: {
                margin: '50px auto',
                maxWidth: '386px'
            },
            '& .stepper-nav-button:last-of-type': { // Hide next button
                visibility: 'hidden'
            },
            '& .stepper-nav-button': {
                width: 'unset' // tightly bounds the nav buttons, so they can be flush against edges of container
            },
            padding: 0,
            '& .step-root': {
                '& .done': {
                    '& .icon-container div': {
                        background: '#3FAD2A',
                        color: '#fff'
                    },
                    '& .primary-title, & .secondary-title': {
                        color: '#30991C'
                    }
                },
                '& .active': {
                    '& .icon-container div': {
                        border: '2px solid #1B8008',
                        background: '#30991C',
                        color: '#fff'
                    },
                    '& .primary-title, & .secondary-title': {
                        color: '#1B8008'
                    }
                },
                '& .inactive': {
                    '& .icon-container div': {
                        background: '#F2F1F1',
                        color: '#A6A6A6'
                    },
                    '& .primary-title, & .secondary-title': {
                        color: '#A6A6A6'
                    }
                }
            }
        },
        currentStep: {
            '& not:(.password-management-form)': {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                textAlign: 'center'
            }
        }
    }
})

const LineConnector = withStyles({
    alternativeLabel: {
        top: 10,
        left: 'calc(-50% + 16px)',
        right: 'calc(50% + 16px)'
    },
    active: {
        '& $line': {
            borderColor: '#3FAD2A'
        }
    },
    completed: {
        '& $line': {
            borderColor: '#3FAD2A'
        }
    },
    line: {
        borderColor: '#eaeaf0',
        borderTopWidth: 1,
        width: '90%',
        margin: 'auto'
    }
})(StepConnector)

const signUpSteps = [
    {
        'data-testid': 'choose-a-plan-step',
        id: 'chooose-a-plan',
        title: 'Choose your plan',
        done: true,
        component: ChooseAPlan
    },
    {
        'data-testid': 'pick-a-number-step',
        id: 'pick-a-number',
        title: 'Pick a number',
        component: PickANumber
    },
    {
        'data-testid': 'checkout-step',
        id: 'checkout',
        title: 'Review & checkout',
        component: CheckoutPage
    }
]

interface StepsProps {
    currentStepId: string
}

/**
 * Renders the current step within the sequence of steps that make up the sign-up flow.
 */
const Steps = ({ currentStepId } : StepsProps) : JSX.Element => {
    const classes = useStyles()

    const { leadParams } = useContext(LeadContext)

    const theme = useTheme()
    const isSmallView = !useMediaQuery(theme.breakpoints.up('md'))

    const history = useHistory()

    const [showStepper, setShowStepper] = useState<boolean>(true)
    const [disableStepper, setDisableStepper] = useState<boolean>(false)

    const stepperSteps = signUpSteps.map(step => ({ ...step, active: step.id === currentStepId }))
    const CurrentStep = signUpSteps.find(step => step.id === currentStepId)?.component

    useEffect(() => {
        if (currentStepId) {
            GTM.reportStepChange(currentStepId)
        }
    }, [currentStepId])

    const goToStep = (stepId : string) => {
        history.push(`/${stepId}`)
    }

    const currentStepIndex = signUpSteps.findIndex(step => step.id === currentStepId)

    const goBack = () => {
        history.goBack()
    }

    if (!currentStepId) {
        goToStep(signUpSteps[0].id)
        return <></>
    }

    const stepInfo = stepperSteps.map((step, idx) => ({ ...step, done: idx < currentStepIndex }))

    useEffect(() => {
        if (['checkout'].includes(currentStepId) && !leadParams.cart.length) {
            // This handles edge cases where the user may click their back button or directly navigate to the final steps before
            // having choose a number.
            alert('You must choose a number before you can proceed to this step.')
            history.push('/pick-a-number')
        }
    }, [currentStepId])

    return (
        <>
            {showStepper &&
                <Stepper
                    smallView={isSmallView}
                    className={classes.stepper}
                    variant={StepperVariant.HORIZONTAL}
                    steps={stepInfo}
                    onBackClick={goBack}
                    onStepClick={stepId => goToStep(stepId)}
                    disabled={disableStepper}
                    connector={<LineConnector />}
                />
            }
            <div className={classes.currentStep}>
                <CurrentStep setShowStepper={setShowStepper} setDisableStepper={setDisableStepper} />
            </div>
        </>
    )
}

export default Steps
